import * as React from 'react';
import { List, Datagrid, TextInput, TextField, SelectInput, SelectField, DateInput, DateField, NumberInput, NumberField, RaRecord, Identifier } from 'react-admin';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CardIssuers } from '../_shared/cardIssuers';

dayjs.extend(utc);

export interface UnifiedCreditTransaction extends RaRecord {
    id: Identifier;
    cardIssuer: string;
    cardName: string;
    lastFour: string;
    amount: number;
    currencyCode: string;
    merchantName: string;
    type: string;
    receiptStatus: string;
    occurredAt: string;
    clearedAt: string;
}

/**
 * Filter controls for the Unified Credit Transactions list.
 */
const postFilters: JSX.Element[] = [
    <SelectInput label="Vendor" source="cardIssuer" choices={CardIssuers} emptyText="ANY" alwaysOn />,
    <TextInput label="Card Name" source="cardName" alwaysOn />,
    <TextInput label="Last Four" source="lastFour" alwaysOn />,
    <DateInput label="Occurred After" source="occurredAt_ge" alwaysOn parse={(date: string) => (date ? new Date(date + 'T00:00:00Z') : null)} />,
    <DateInput label="Occurred Before" source="occurredAt_le" alwaysOn parse={(date: string) => (date ? new Date(date + 'T23:59:59Z') : null)} />,
    <NumberInput label="Amount from" source="amount_ge" sx={{ width: 130 }} alwaysOn />,
    <NumberInput label="Amount to" source="amount_le" sx={{ width: 130 }} alwaysOn />,
    <TextInput label="Merchant" source="merchantName" alwaysOn />,
];

const UnifiedCreditTransactionsList = () => (
    <List
        storeKey={false}
        disableSyncWithLocation
        exporter={false}
        perPage={25}
        sort={{ field: 'occurredAt', order: 'DESC' }}
        filters={postFilters}
        filterDefaultValues={{
            'occurredAt_ge': dayjs().utc().startOf('day').subtract(15, 'day').toISOString(),
        }}
    >
        <Datagrid bulkActionButtons={false}>
            <SelectField source="cardIssuer" label="Vendor" choices={CardIssuers} translateChoice={false} />
            <TextField source="cardName" label="Card Name" />
            <TextField source="lastFour" label="Last Four" />
            <NumberField source="amount" label="Amount" options={{ style: 'currency', currency: 'USD' }} />
            <TextField source="currencyCode" label="Currency" />
            <TextField source="merchantName" label="Merchant" />
            <TextField source="type" label="Type" />
            <TextField source="receiptStatus" label="Receipt Status" />
            <DateField source="occurredAt" label="Occurred At" showTime />
            <DateField source="clearedAt" label="Cleared At" showTime />
        </Datagrid>
    </List>
);

export interface UnifiedCreditTransactionsPageResource {
    name: string;
    list: React.FC;
}

/**
 * Unified Credit Transactions page - lists the unified `credit_card_transactions` table
 * populated from Divvy, Privacy.com, and Emburse.
 */
const UnifiedCreditTransactionsPage: UnifiedCreditTransactionsPageResource = {
    name: 'UnifiedCreditTransactions',
    list: UnifiedCreditTransactionsList
};

export default UnifiedCreditTransactionsPage;
